import React, { useState, useEffect, useRef, useCallback, useMemo, forwardRef, useImperativeHandle } from 'react';
import SimpleCard from '../Components/Cards/SimpleCard';
import FilterComponent, { TagItem } from '../Components/Filters/FilterComponent';
import SimpleCatalogSelect from './SimpleCatalogSelect';
import { useSelector, useDispatch } from "react-redux";
import dayjs from "dayjs";
import { Select, Typography } from 'antd';
import SimpleProductSelect from './SimpleProductSelect';
import { setSelectedCatalogue, setSelectedCatalogues } from '../../redux/Catalogues/actions';

const { Text } = Typography;
const { Option } = Select;

// Funzione di utilità per verificare se due date sono uguali (anno, mese, giorno)
const isSameDay = (date1, date2) => {
    if (!date1 || !date2) return false;
    return (
        date1.year() === date2.year() &&
        date1.month() === date2.month() &&
        date1.date() === date2.date()
    );
};

// Funzione per verificare se un intervallo di date corrisponde a un preset
const findPresetLabel = (currentRange, presets) => {
    if (!currentRange || !currentRange[0] || !currentRange[1] || !presets) return null;

    // Verifichiamo se l'intervallo corrente corrisponde a uno dei preset
    for (const preset of presets) {
        const presetStart = preset.value[0];
        const presetEnd = preset.value[1];

        if (isSameDay(currentRange[0], presetStart) && isSameDay(currentRange[1], presetEnd)) {
            return preset.label;
        }
    }

    return null;
};

const Filters = forwardRef(({
    // Proprietà originali di Filters
    typeData,
    setTypeData,
    onChangeService,
    selectedValue,
    setSelectedValue,
    setLoadingSelect,
    loadingSelect,

    // Proprietà di FiltersTop
    dateRange,
    onChangeDate,
    onClearFilter
}, ref) => {
    // Ref per accedere ai metodi di SimpleProductSelect
    const productSelectRef = useRef(null);
    const dispatch = useDispatch();

    // Otteniamo sia selectedCatalog (singolo) che selectedCatalogs (array)
    const selectedCatalogs = useSelector((state) => state.catalogues.selectedCatalogs || []);
    const catalogsList = useSelector((state) => state.catalogues.listaCatalogues);
    const productsList = useSelector((state) => state.catalogues.listaProdotti);

    // Stato locale per tenere traccia delle selezioni di filtro (senza ancora applicarli)
    const [selectedFilters, setSelectedFilters] = useState({
        catalogs: selectedCatalogs?.length > 0 ? selectedCatalogs.map(cat => cat.id) : ['all'],
        catalogObjects: selectedCatalogs?.length > 0 ? selectedCatalogs : [{ id: 'all', nameCatalog: 'All Catalogues' }],
        products: [],
        productDisplayNames: [], // Array invece di singola stringa
        service: Array.isArray(typeData) ? typeData : [typeData]
    });

    // Flag per tenere traccia se i filtri sono stati applicati
    const [filtersApplied, setFiltersApplied] = useState(false);

    // Flag per tracciare l'apertura del dropdown del filtro
    const [isFilterOpen, setIsFilterOpen] = useState(false);

    // Stato per il label del preset
    const [currentPresetLabel, setCurrentPresetLabel] = useState(null);

    // Utilizziamo un ref per tracciare lo stato precedente e prevenire aggiornamenti inutili
    const prevFiltersRef = useRef({
        catalogs: selectedCatalogs?.length > 0 ? selectedCatalogs.map(cat => cat.id) : ['all'],
        catalogObjects: selectedCatalogs?.length > 0 ? selectedCatalogs : [{ id: 'all', nameCatalog: 'All Catalogues' }],
        products: [],
        productDisplayNames: [], // Array invece di singola stringa
        service: Array.isArray(typeData) ? typeData : [typeData]
    });

    // Esponiamo funzioni per il componente genitore
    useImperativeHandle(ref, () => ({
        // Funzione per applicare i filtri manualmente
        applyFilters: () => {
            handleFiltersApply();
        },
        // Funzione per resettare i filtri
        resetFilters: () => {
            handleFiltersClear();
        },
        // Funzione per ottenere lo stato corrente dei filtri
        getSelectedFilters: () => {
            return { ...selectedFilters };
        },
        // Funzione per resettare date e servizi quando si arriva da MyCatalogues
        resetDateAndServiceFilters: () => {
            // Reset date range al default (ultimi 7 giorni)
            const defaultRange = [
                dayjs().subtract(7, "day").startOf("day"),
                dayjs().endOf("day")
            ];
            handleDateRangeChange(defaultRange);

            // Reset servizi a "all"
            setSelectedFilters(prev => ({
                ...prev,
                service: ['all']
            }));
            setTypeData(['all']);
            onChangeService(['all']);
        }
    }));

    // Opzioni per il select dei servizi
    const serviceOptions = [
        { value: 'app', label: 'VTO Apps' },
        { value: '3d', label: '3D/AR Viewer' },
        { value: 'WebVto', label: 'Web VTO' },
        { value: 'all', label: 'All' }
    ];

    // Configurazione presets date range in useMemo
    const rangePresets = useMemo(() => [
        { label: "Today", value: [dayjs().startOf("day"), dayjs().endOf("day")] },
        { label: "Yesterday", value: [dayjs().subtract(1, "day").startOf("day"), dayjs().subtract(1, "day").endOf("day")] },
        { label: "Last Week", value: [dayjs().subtract(1, "week").startOf("week"), dayjs().subtract(1, "week").endOf("week")] },
        { label: "This Week", value: [dayjs().startOf("week"), dayjs().endOf("day")] },
        { label: "Last Month", value: [dayjs().subtract(1, "month").startOf("month"), dayjs().subtract(1, "month").endOf("month")] },
        { label: "This Month", value: [dayjs().startOf("month"), dayjs().endOf("day")] },
        { label: "Last 3 Months", value: [dayjs().subtract(3, "month").startOf("day"), dayjs().endOf("day")] },
        { label: "Last 6 Months", value: [dayjs().subtract(6, "month").startOf("day"), dayjs().endOf("day")] },
        { label: "This Year", value: [dayjs().startOf("year"), dayjs().endOf("day")] },
    ], []);

    // Aggiorniamo il label del preset quando cambia l'intervallo di date
    useEffect(() => {
        const label = findPresetLabel(dateRange, rangePresets);

        // Verifichiamo se l'intervallo corrente è quello di default (ultimi 7 giorni)
        const isDefaultDateRange = dateRange &&
            dateRange[0] &&
            dateRange[1] &&
            isSameDay(dateRange[0], dayjs().subtract(7, 'day')) &&
            isSameDay(dateRange[1], dayjs());

        setCurrentPresetLabel(isDefaultDateRange ? "Last 7 days" : label);
    }, [dateRange, rangePresets]);

    // Sincronizza selectedFilters con i cambiamenti esterni
    useEffect(() => {
        const newCatalogs = selectedCatalogs?.length > 0 ? selectedCatalogs.map(cat => cat.id) : ['all'];
        const newCatalogObjects = selectedCatalogs?.length > 0 ? selectedCatalogs : [{ id: 'all', nameCatalog: 'All Catalogues' }];
        const newService = Array.isArray(typeData) ? typeData : [typeData];

        // Controlla se stiamo arrivando da MyCatalogues con un catalogo specifico
        const isComingFromMyCatalogues = selectedCatalogs?.length > 0 &&
            selectedCatalogs[0].id !== 'all' &&
            !filtersApplied;

        setSelectedFilters(prev => {
            const newFilters = {
                ...prev,
                catalogs: newCatalogs,
                catalogObjects: newCatalogObjects,
                service: newService
            };

            // Se stiamo arrivando da MyCatalogues, reset anche prodotti e altri filtri
            if (isComingFromMyCatalogues) {
                newFilters.products = [];
                newFilters.productDisplayNames = [];
                newFilters.service = ['all']; // Reset servizi a "all"

                // Imposta i filtri come applicati per mostrare i tag immediatamente
                setFiltersApplied(true);

                // Reset date range e servizi
                setTimeout(() => {
                    // Reset date range al default (ultimi 7 giorni)
                    const defaultRange = [
                        dayjs().subtract(7, "day").startOf("day"),
                        dayjs().endOf("day")
                    ];
                    handleDateRangeChange(defaultRange);

                    // Reset servizi
                    setTypeData(['all']);
                    onChangeService(['all']);
                }, 0);
            }

            return newFilters;
        });

        prevFiltersRef.current = {
            ...prevFiltersRef.current,
            catalogs: newCatalogs,
            catalogObjects: newCatalogObjects,
            service: newService
        };
    }, [selectedCatalogs, typeData]);

    // Funzione di utilità per verificare se gli oggetti di filtro sono uguali
    const areFiltersEqual = (f1, f2) => {
        // Verifica catalogs
        if (f1.catalogs.length !== f2.catalogs.length) return false;
        const sortedCat1 = [...f1.catalogs].sort();
        const sortedCat2 = [...f2.catalogs].sort();
        if (!sortedCat1.every((val, idx) => val === sortedCat2[idx])) return false;

        // Verifica products
        if (f1.products.length !== f2.products.length) return false;
        const sortedProducts1 = [...f1.products].sort();
        const sortedProducts2 = [...f2.products].sort();
        if (!sortedProducts1.every((val, idx) => val === sortedProducts2[idx])) return false;

        // Verifica productDisplayNames
        if (f1.productDisplayNames.length !== f2.productDisplayNames.length) return false;
        const sortedDisplayNames1 = [...f1.productDisplayNames].sort();
        const sortedDisplayNames2 = [...f2.productDisplayNames].sort();
        if (!sortedDisplayNames1.every((val, idx) => val === sortedDisplayNames2[idx])) return false;

        // Verifica service
        if (f1.service.length !== f2.service.length) return false;
        const sortedServices1 = [...f1.service].sort();
        const sortedServices2 = [...f2.service].sort();
        if (!sortedServices1.every((val, idx) => val === sortedServices2[idx])) return false;

        // Verifica catalogObjects (optional, solo se entrambi li hanno)
        if (f1.catalogObjects && f2.catalogObjects) {
            if (f1.catalogObjects.length !== f2.catalogObjects.length) return false;
            const sortedCatObj1 = [...f1.catalogObjects].sort((a, b) => a.id.localeCompare(b.id));
            const sortedCatObj2 = [...f2.catalogObjects].sort((a, b) => a.id.localeCompare(b.id));
            if (!sortedCatObj1.every((val, idx) => val.id === sortedCatObj2[idx].id)) return false;
        }

        return true;
    };

    // Handler per il cambio del servizio
    const handleServiceChange = useCallback((value) => {
        // Verifica se "all" è stato appena selezionato
        if (value.includes('all') && !selectedFilters.service.includes('all')) {
            // Se "all" è selezionato, deseleziona tutti gli altri
            setSelectedFilters(prev => {
                const newFilters = { ...prev, service: ['all'] };
                if (!areFiltersEqual(newFilters, prevFiltersRef.current)) {
                    prevFiltersRef.current = { ...newFilters };
                    return newFilters;
                }
                return prev;
            });
        }
        // Verifica se sono stati selezionati altri servizi (non "all")
        else if (value.some(item => item !== 'all')) {
            // Filtra "all" se presente e mantiene solo gli altri servizi
            const filteredValues = value.filter(item => item !== 'all');
            setSelectedFilters(prev => {
                const newFilters = { ...prev, service: filteredValues };
                if (!areFiltersEqual(newFilters, prevFiltersRef.current)) {
                    prevFiltersRef.current = { ...newFilters };
                    return newFilters;
                }
                return prev;
            });
        }
        // Se l'utente ha deselezionato tutto, imposta "all" come default
        else if (value.length === 0) {
            setSelectedFilters(prev => {
                const newFilters = { ...prev, service: ['all'] };
                if (!areFiltersEqual(newFilters, prevFiltersRef.current)) {
                    prevFiltersRef.current = { ...newFilters };
                    return newFilters;
                }
                return prev;
            });
        }
        // Altrimenti imposta il valore come è
        else {
            setSelectedFilters(prev => {
                const newFilters = { ...prev, service: value };
                if (!areFiltersEqual(newFilters, prevFiltersRef.current)) {
                    prevFiltersRef.current = { ...newFilters };
                    return newFilters;
                }
                return prev;
            });
        }
    }, [selectedFilters, areFiltersEqual]);

    // Handler per la selezione dei cataloghi
    const handleCatalogSelectionChange = useCallback((selectedValues, selectedCatalogObjects) => {
        // Non aggiornare lo stato se i valori sono gli stessi
        if (JSON.stringify(selectedFilters.catalogs) === JSON.stringify(selectedValues)) {
            return;
        }

        setSelectedFilters(prev => {
            const newFilters = {
                ...prev,
                catalogs: selectedValues,
                catalogObjects: selectedCatalogObjects || [] // Memorizza anche gli oggetti completi
            };
            // Aggiorna solo se è cambiato veramente
            if (!areFiltersEqual(newFilters, prevFiltersRef.current)) {
                prevFiltersRef.current = { ...newFilters };
                return newFilters;
            }
            return prev;
        });
    }, [selectedFilters.catalogs]);

    // Handler per la selezione dei prodotti - ora memorizza solo i dati localmente
    const handleProductSelectionChange = useCallback((selection) => {
        setSelectedFilters(prev => {
            // La selezione ora contiene 'ids' (array di ID) e 'displayNames' (array di nomi di modelli)
            const newProducts = selection.ids || [];

            // Salviamo anche i displayNames per visualizzare i tag corretti
            const newFilters = {
                ...prev,
                products: newProducts,
                productDisplayNames: selection.displayNames || []
            };

            // Aggiorna solo se è cambiato veramente
            if (!areFiltersEqual(newFilters, prevFiltersRef.current)) {
                prevFiltersRef.current = { ...newFilters };
                return newFilters;
            }
            return prev;
        });
    }, []);

    // Handler per applicare i filtri
    const handleFiltersApply = useCallback(() => {
        setIsFilterOpen(false);
        setFiltersApplied(true);

        // Applica i filtri di servizio direttamente
        onChangeService(selectedFilters.service);
        setTypeData(selectedFilters.service);

        // Applica i filtri dei cataloghi
        if (selectedFilters.catalogs.length === 1 && selectedFilters.catalogs[0] === 'all') {
            const allCatalog = { id: "all", nameCatalog: "All Catalogues" };
            dispatch(setSelectedCatalogue(allCatalog));
            dispatch(setSelectedCatalogues([allCatalog]));
        } else {
            // Usa direttamente selectedFilters.catalogObjects che contiene già i nomi completi
            const selectedCatalogObjects = selectedFilters.catalogObjects || [];

            dispatch(setSelectedCatalogues(selectedCatalogObjects));
            dispatch(setSelectedCatalogue(selectedCatalogObjects[0]));
        }

        // Usa la funzione applyFilters di SimpleProductSelect per applicare il filtro dei prodotti
        if (productSelectRef.current) {
            productSelectRef.current.applyFilters(selectedFilters.products);
        }
    }, [selectedFilters, onChangeService, setTypeData, dispatch, catalogsList]);

    // Handler per cancellare i filtri (esclude le date)
    const handleFiltersClear = useCallback(() => {
        const defaultFilters = {
            catalogs: ['all'],
            catalogObjects: [{ id: 'all', nameCatalog: 'All Catalogues' }],
            products: [],
            productDisplayNames: [],
            service: ["all"]
        };

        // Reset lo stato locale dei filtri
        setSelectedFilters(defaultFilters);
        prevFiltersRef.current = { ...defaultFilters };

        // Reset la selezione in SimpleProductSelect
        if (productSelectRef.current) {
            productSelectRef.current.resetSelection();
        }

        // Reset la UI
        setTypeData(["all"]);

        // Applica i filtri resettati senza toccare le date
        // Applica i filtri dei cataloghi
        const allCatalog = { id: "all", nameCatalog: "All Catalogues" };
        dispatch(setSelectedCatalogue(allCatalog));
        dispatch(setSelectedCatalogues([allCatalog]));

        // Applica i filtri di servizio
        onChangeService(["all"]);

        setFiltersApplied(false);
    }, [onChangeService, setTypeData, dispatch]);

    // Handler per la modifica della data - applica immediatamente
    const handleDateRangeChange = useCallback((dates) => {
        if (onChangeDate) {
            onChangeDate(dates);
        }

        // Aggiorniamo il label del preset
        const label = findPresetLabel(dates, rangePresets);
        setCurrentPresetLabel(label);
    }, [onChangeDate, rangePresets]);

    // Handler per apertura/chiusura del filtro
    const toggleFilter = useCallback(() => {
        setIsFilterOpen(prev => !prev);
    }, []);

    // Verifica se ci sono filtri attivi
    const hasSelectedFilters = useMemo(() => {
        // Verifichiamo se tra i cataloghi selezionati c'è solo "All"
        const isCatalogAll = selectedFilters.catalogs.length === 1 &&
            selectedFilters.catalogs[0] === 'all';

        // Se c'è solo "All" come catalogo, lo consideriamo come nessun catalogo selezionato
        const hasCatalogs = selectedFilters.catalogs.length > 0 && !isCatalogAll;

        // Verifichiamo se i servizi includono solo "all" o se ci sono altri servizi
        const isServiceAll = selectedFilters.service.length === 1 && selectedFilters.service[0] === 'all';

        // Verifichiamo se ci sono altri filtri attivi
        return hasCatalogs ||
            !isServiceAll ||
            selectedFilters.products.length > 0;
    }, [selectedFilters.catalogs, selectedFilters.catalogObjects, selectedFilters.service, selectedFilters.products]);

    // Funzione per renderizzare i filtri selezionati
    const renderSelectedFilters = useCallback((inFilterDropdown) => {
        // Mostra i tag sempre quando ci sono filtri selezionati, non solo quando applicati
        // Oppure quando i filtri sono stati applicati (per compatibilità con il flusso normale)
        const hasAnySelectedFilters = selectedFilters.catalogs.length > 0 &&
            !(selectedFilters.catalogs.length === 1 && selectedFilters.catalogs[0] === 'all') ||
            selectedFilters.products.length > 0 ||
            selectedFilters.service.length > 0 && !selectedFilters.service.includes('all');

        // Mostra i tag se ci sono filtri selezionati E (filtri applicati O se stiamo arrivando da MyCatalogues)
        const shouldShowTags = hasAnySelectedFilters && (filtersApplied ||
            (selectedFilters.catalogs.length > 0 && selectedFilters.catalogs[0] !== 'all'));

        if (!shouldShowTags) return null;

        // Cataloghi semplificati per il rendering - usa selectedFilters.catalogObjects che contiene già i nomi
        const simpleCatalogs = selectedFilters.catalogObjects || [];

        // Contiamo i tipi di filtri attivi per determinare se è presente una sola tipologia
        const activeFilterTypes = [
            simpleCatalogs.length > 0 && !(simpleCatalogs.length === 1 && simpleCatalogs[0].id === 'all') ? 1 : 0,
            selectedFilters.service.length > 0 && !selectedFilters.service.includes('all') ? 1 : 0,
            selectedFilters.products.length > 0 ? 1 : 0
        ].reduce((sum, val) => sum + val, 0);

        // Determiniamo se mostrare o meno il marginBottom
        const shouldShowMargin = activeFilterTypes > 1;

        // Stile aggiuntivo per il container dei filtri quando non è nel dropdown
        const containerStyle = !inFilterDropdown ? { marginTop: 'clamp(0.5rem, 2vw, 1rem)' } : {};

        return (
            <div style={containerStyle}>
                {/* Catalog Section - Non mostriamo nulla se è solo "all" */}
                {simpleCatalogs.length > 0 && !(simpleCatalogs.length === 1 && simpleCatalogs[0].id === 'all') && (
                    <div style={{ marginBottom: shouldShowMargin ? 'clamp(0.5rem, 1vw, 0.75rem)' : '0' }}>
                        <div>
                            {simpleCatalogs.map(catalog => (
                                <TagItem
                                    key={catalog?.id || 'unknown'}
                                >
                                    {catalog?.nameCatalog || 'Unknown'}
                                </TagItem>
                            ))}
                            <Text style={{
                                display: 'inline-block',
                                marginLeft: 'clamp(0.25rem, 0.5vw, 0.375rem)',
                                verticalAlign: 'middle'
                            }}>
                                {simpleCatalogs.length > 1 ?
                                    `${simpleCatalogs.length} catalogues selected` :
                                    'Catalog selected'}
                            </Text>
                        </div>
                    </div>
                )}

                {/* Service Section */}
                {selectedFilters.service.length > 0 && !selectedFilters.service.includes('all') && (
                    <div style={{ marginBottom: shouldShowMargin ? 'clamp(0.5rem, 1vw, 0.75rem)' : '0' }}>
                        <div>
                            {selectedFilters.service.map(service => (
                                <TagItem key={service}>
                                    {service === 'app' ? 'VTO Apps' :
                                        service === '3d' ? '3D/AR Viewer' :
                                            service === 'WebVto' ? 'Web VTO' :
                                                service}
                                </TagItem>
                            ))}
                            <Text style={{
                                display: 'inline-block',
                                marginLeft: 'clamp(0.25rem, 0.5vw, 0.375rem)',
                                verticalAlign: 'middle'
                            }}>
                                {selectedFilters.service.length > 1 ?
                                    `${selectedFilters.service.length} services selected` :
                                    'Service selected'}
                            </Text>
                        </div>
                    </div>
                )}

                {/* Products Section */}
                {selectedFilters.products.length > 0 && (
                    <div style={{ marginBottom: '0' }}>
                        <div>
                            {/* Mostriamo un tag per ogni nome di modello */}
                            {selectedFilters.productDisplayNames.map(displayName => (
                                <TagItem key={displayName}>
                                    {displayName}
                                </TagItem>
                            ))}
                            <Text style={{
                                display: 'inline-block',
                                marginLeft: 'clamp(0.25rem, 0.5vw, 0.375rem)',
                                verticalAlign: 'middle'
                            }}>
                                {selectedFilters.products.length > 1 ?
                                    `${selectedFilters.products.length} products selected` :
                                    'Product selected'}
                            </Text>
                        </div>
                    </div>
                )}
            </div>
        );
    }, [selectedFilters.catalogs, selectedFilters.catalogObjects, selectedFilters.service, selectedFilters.products, selectedFilters.productDisplayNames]);

    // Prepariamo i componenti per le varie sezioni
    const catalogSectionComponent = (
        <SimpleCatalogSelect
            catalogsList={catalogsList || []}
            onChange={handleCatalogSelectionChange}
            isFilterOpen={isFilterOpen}
        />
    );

    const productsSectionComponent = (
        <SimpleProductSelect
            ref={productSelectRef}
            selectedValue={selectedValue}
            setSelectedValue={setSelectedValue}
            loadingSelect={loadingSelect}
            setLoadingSelect={setLoadingSelect}
            onChange={handleProductSelectionChange}
            selectedCatalogs={selectedFilters.catalogObjects}
        />
    );

    const serviceSectionComponent = (
        <Select
            mode="multiple"
            value={selectedFilters.service}
            onChange={handleServiceChange}
            style={{
                width: "100%",
                borderRadius: "8px",
                borderColor: !selectedFilters.service.includes("all") ? "rgba(66, 177, 172, 1)" : undefined,
                boxShadow: !selectedFilters.service.includes("all") ? "0 0 0 2px rgba(66, 177, 172, 1)" : undefined,
            }}
            placeholder="Select services"
        >
            {serviceOptions.map(option => (
                <Option key={option.value} value={option.value}>
                    {option.label}
                </Option>
            ))}
        </Select>
    );

    return (
        <SimpleCard>
            <FilterComponent
                // Stato e controllo del filtro
                isFilterOpen={isFilterOpen}
                onToggleFilter={toggleFilter}

                // Props per il date picker
                dateRange={dateRange}
                onChangeDateRange={handleDateRangeChange}
                presetLabel={currentPresetLabel}
                rangePresets={rangePresets}

                // Props per i filtri
                onClearFilters={handleFiltersClear}
                onApplyFilter={handleFiltersApply}

                // Componenti per le sezioni
                catalogSection={catalogSectionComponent}
                productsSection={productsSectionComponent}
                serviceSection={serviceSectionComponent}

                // Props per i filtri selezionati
                renderSelectedFilters={renderSelectedFilters}
                hasSelectedFilters={hasSelectedFilters}
                selectedCatalogs={selectedCatalogs}
                selectedProducts={selectedFilters.products}
                serviceValue={typeData}
                isFiltersApplied={filtersApplied}
            />
        </SimpleCard>
    );
});

export default Filters;